import React, { useState, useEffect } from 'react';
import { 
  X, Filter, Calendar, Check, RefreshCw, Search, AlertCircle
} from 'lucide-react';
import config from '../config';

const CommissionStatusModal = ({ 
  isOpen, 
  onClose, 
  clients, 
  delegateId, 
  onFixComplete 
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [search, setSearch] = useState('');
  
  // حالة النافذة
  const [step, setStep] = useState('select'); // 'select', 'confirm', 'success'
  
  // فلاتر متقدمة
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilters, setDateFilters] = useState({
    travelDateFrom: '',
    travelDateTo: '',
    commissionDateFrom: '',
    commissionDateTo: ''
  });
  
  // نوع تصحيح حالة العمولة (مدفوعة أو غير مدفوعة)
  const [targetStatus, setTargetStatus] = useState('paid');
  
  // فلترة بسيطة
  const [filterType, setFilterType] = useState('all'); // 'all', 'hasCommission', 'noCommission', 'hasTraveled'
  
  useEffect(() => {
    if (isOpen && clients) {
      // تطبيق الفلترة الأولية
      applyFilters();
    }
  }, [isOpen, clients, filterType, dateFilters, search]);
  
  // تطبيق الفلترة المركبة
  const applyFilters = () => {
    if (!clients) return;
    
    let filtered = [...clients];
    
    // تطبيق فلترة حسب النوع
    if (filterType === 'hasCommission') {
      filtered = filtered.filter(client => 
        client.commissionStatus === 'مدفوعة' || 
        (client.commissions && client.commissions.length > 0)
      );
    } else if (filterType === 'noCommission') {
      filtered = filtered.filter(client => 
        client.commissionStatus !== 'مدفوعة' && 
        (!client.commissions || client.commissions.length === 0)
      );
    } else if (filterType === 'hasTraveled') {
      filtered = filtered.filter(client => client.TravelDate);
    }
    
    // تطبيق فلترة حسب التاريخ
    if (dateFilters.travelDateFrom) {
      filtered = filtered.filter(client => 
        client.TravelDate && new Date(client.TravelDate) >= new Date(dateFilters.travelDateFrom)
      );
    }
    
    if (dateFilters.travelDateTo) {
      filtered = filtered.filter(client => 
        client.TravelDate && new Date(client.TravelDate) <= new Date(dateFilters.travelDateTo)
      );
    }
    
    if (dateFilters.commissionDateFrom) {
      filtered = filtered.filter(client => 
        client.CommissionPaymentDate && 
        new Date(client.CommissionPaymentDate) >= new Date(dateFilters.commissionDateFrom)
      );
    }
    
    if (dateFilters.commissionDateTo) {
      filtered = filtered.filter(client => 
        client.CommissionPaymentDate && 
        new Date(client.CommissionPaymentDate) <= new Date(dateFilters.commissionDateTo)
      );
    }
    
    // تطبيق البحث
    if (search) {
      filtered = filtered.filter(client => 
        (client.NameArabic && client.NameArabic.includes(search)) ||
        (client.PassportNumber && client.PassportNumber.includes(search)) ||
        (client.CompanyName && client.CompanyName.includes(search))
      );
    }
    
    setFilteredClients(filtered);
  };
  
  // تحديد الكل
  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(client => client.PassportID));
    }
    setAllSelected(!allSelected);
  };
  
  // تحديد/إلغاء تحديد عميل واحد
  const handleSelectClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(prev => prev.filter(id => id !== clientId));
    } else {
      setSelectedClients(prev => [...prev, clientId]);
    }
  };
  
  // البحث في العملاء
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };
  
  // تغيير فلترة التاريخ
  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // تنفيذ عملية تصحيح حالة العمولة
  const handleFixCommissionStatus = async () => {
    if (selectedClients.length === 0) {
      alert('الرجاء اختيار عميل واحد على الأقل');
      return;
    }
    
    setLoading(true);
    
    try {
     const response = await fetch(`${config.API_BASE_URL}/api/fix-commission-status`, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
      body: JSON.stringify({
  clientIds: selectedClients,
  delegateId: delegateId,
  targetStatus: targetStatus,
  travelDateFrom: dateFilters.travelDateFrom || undefined,
  travelDateTo: dateFilters.travelDateTo || undefined,
  commissionDateFrom: dateFilters.commissionDateFrom || undefined,
  commissionDateTo: dateFilters.commissionDateTo || undefined
})
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setStep('success');
        // استدعاء دالة استكمال العملية لتحديث البيانات في الواجهة الرئيسية
        if (onFixComplete) {
          onFixComplete(result);
        }
      } else {
        throw new Error(result.error || 'حدث خطأ أثناء تصحيح حالة العمولة');
      }
    } catch (error) {
      console.error('خطأ في تصحيح حالة العمولة:', error);
      alert(`حدث خطأ: ${error.message}`);
      setStep('select');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center" onClick={() => step !== 'confirm' && step !== 'loading' && onClose()}>
      <div 
        className="relative p-0 border w-full max-w-4xl bg-white shadow-lg rounded-lg"
        onClick={e => e.stopPropagation()}
      >
        {/* رأس النافذة */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 py-4 px-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-medium text-white">
              تصحيح حالة العمولة
            </h3>
            <button
              type="button"
              className="text-white hover:text-gray-200 transition-colors"
              onClick={onClose}
              disabled={loading || step === 'confirm'}
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        {/* محتوى النافذة - الخطوة 1: اختيار العملاء */}
        {step === 'select' && (
          <div className="p-6">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium text-gray-700">
                  اختر العملاء لتصحيح حالة العمولة
                </h4>
                
                <div className="flex gap-2">
                  {/* زر إظهار/إخفاء فلاتر متقدمة */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 rounded transition-colors"
                  >
                    <Filter size={16} />
                    <span>فلترة متقدمة</span>
                  </button>
                  
                  {/* البحث */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="بحث..."
                      value={search}
                      onChange={handleSearch}
                      className="pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              
              {/* فلترة بسيطة */}
              <div className="flex space-x-2 rtl:space-x-reverse mb-4">
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filterType === 'all'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  جميع العملاء
                </button>
                <button
                  onClick={() => setFilterType('hasTraveled')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filterType === 'hasTraveled'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  المسافرين فقط
                </button>
                <button
                  onClick={() => setFilterType('hasCommission')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filterType === 'hasCommission'
                    ? 'bg-purple-100 text-purple-700 border border-purple-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  تم دفع العمولة
                </button>
                <button
                  onClick={() => setFilterType('noCommission')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filterType === 'noCommission'
                    ? 'bg-amber-100 text-amber-700 border border-amber-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  لم يتم دفع العمولة
                </button>
              </div>
              
              {/* فلاتر متقدمة */}
              {showFilters && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h5 className="font-medium text-gray-700 mb-3">فلترة حسب التاريخ</h5>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h6 className="text-sm font-medium text-gray-600 mb-2">تاريخ السفر</h6>
                      <div className="flex gap-2 items-center">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">من</label>
                          <div className="relative">
                            <input
                              type="date"
                              name="travelDateFrom"
                              value={dateFilters.travelDateFrom}
                              onChange={handleDateFilterChange}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            />
                            <Calendar className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">إلى</label>
                          <div className="relative">
                            <input
                              type="date"
                              name="travelDateTo"
                              value={dateFilters.travelDateTo}
                              onChange={handleDateFilterChange}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            />
                            <Calendar className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h6 className="text-sm font-medium text-gray-600 mb-2">تاريخ دفع العمولة</h6>
                      <div className="flex gap-2 items-center">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">من</label>
                          <div className="relative">
                            <input
                              type="date"
                              name="commissionDateFrom"
                              value={dateFilters.commissionDateFrom}
                              onChange={handleDateFilterChange}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            />
                            <Calendar className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">إلى</label>
                          <div className="relative">
                            <input
                              type="date"
                              name="commissionDateTo"
                              value={dateFilters.commissionDateTo}
                              onChange={handleDateFilterChange}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            />
                            <Calendar className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => {
                        setDateFilters({
                          travelDateFrom: '',
                          travelDateTo: '',
                          commissionDateFrom: '',
                          commissionDateTo: ''
                        });
                      }}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      إعادة ضبط الفلاتر
                    </button>
                  </div>
                </div>
              )}
              
              {/* اختيار نوع تصحيح الحالة */}
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-gray-700 mb-2">نوع تصحيح حالة العمولة:</h5>
                <div className="flex space-x-4 rtl:space-x-reverse">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-blue-600"
                      name="targetStatus"
                      value="paid"
                      checked={targetStatus === 'paid'}
                      onChange={() => setTargetStatus('paid')}
                    />
                    <span className="mr-2">تعيين كمدفوعة</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-red-600"
                      name="targetStatus"
                      value="unpaid"
                      checked={targetStatus === 'unpaid'}
                      onChange={() => setTargetStatus('unpaid')}
                    />
                    <span className="mr-2">تعيين كغير مدفوعة</span>
                  </label>
                </div>
              </div>
              
              {/* جدول العملاء */}
              <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-3 text-center">
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                        </div>
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500">اسم العميل</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500">الشركة</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500">تاريخ السفر</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500">حالة العمولة</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500">تاريخ دفع العمولة</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClients.length > 0 ? (
                      filteredClients.map(client => (
                        <tr key={client.PassportID} className="hover:bg-gray-50">
                          <td className="px-3 py-4 text-center">
                            <div className="flex justify-center">
                              <input
                                type="checkbox"
                                checked={selectedClients.includes(client.PassportID)}
                                onChange={() => handleSelectClient(client.PassportID)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {client.NameArabic}
                            </div>
                            <div className="text-xs text-gray-500">
                              {client.PassportNumber}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {client.CompanyName || '-'}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            {client.TravelDate ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {new Date(client.TravelDate).toLocaleDateString('ar-EG')}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            {client.TravelDate ? (
                              client.commissionStatus === 'مدفوعة' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Check size={12} className="ml-1" />
                                  مدفوعة
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  <X size={12} className="ml-1" />
                                  غير مدفوعة
                                </span>
                              )
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {client.CommissionPaymentDate ? 
                                new Date(client.CommissionPaymentDate).toLocaleDateString('ar-EG') : '-'}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          لا توجد نتائج تطابق معايير البحث
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* تذييل العملية */}
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  تم تحديد {selectedClients.length} من {filteredClients.length} عميل
                </div>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded"
                  >
                    إلغاء
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setStep('confirm')}
                    disabled={selectedClients.length === 0}
                    className={`px-4 py-2 rounded ${
                      selectedClients.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    تصحيح حالة العمولة
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* الخطوة 2: تأكيد العملية */}
        {step === 'confirm' && (
          <div className="p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="text-amber-500 mt-0.5 ml-3 flex-shrink-0" size={18} />
                <div>
                  <h4 className="font-medium text-amber-800 mb-1">تأكيد تصحيح حالة العمولة</h4>
                  <p className="text-sm text-amber-700">
                    سيتم تصحيح حالة العمولة لـ {selectedClients.length} عميل إلى "{targetStatus === 'paid' ? 'مدفوعة' : 'غير مدفوعة'}". هل أنت متأكد من المتابعة؟
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 mb-6">
              <h5 className="font-medium text-gray-700 mb-2">ملخص العملية:</h5>
              <ul className="text-sm text-gray-600 space-y-1 mb-4 list-disc list-inside mr-4">
                <li>عدد العملاء المحددين: {selectedClients.length}</li>
                <li>سيتم تعيين حالة العمولة إلى "{targetStatus === 'paid' ? 'مدفوعة' : 'غير مدفوعة'}"</li>
                <li>{targetStatus === 'paid' ? 'سيتم تعيين تاريخ الدفع إلى التاريخ الحالي' : 'سيتم إزالة تاريخ الدفع'}</li>
              </ul>
            </div>
            
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setStep('select')}
                disabled={loading}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded"
              >
                رجوع
              </button>
              
              <button
                type="button"
                onClick={handleFixCommissionStatus}
                disabled={loading}
                className={`flex items-center gap-1 px-4 py-2 rounded ${
                  loading
                  ? 'bg-gray-400 text-gray-700 cursor-wait'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
                }`}
              >
                {loading && <RefreshCw className="animate-spin" size={16} />}
                تأكيد العملية
              </button>
            </div>
          </div>
        )}
        
        {/* الخطوة 3: نجاح العملية */}
        {step === 'success' && (
          <div className="p-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-6 text-center">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Check className="text-green-600" size={36} />
                </div>
                <h4 className="text-xl font-medium text-green-800 mb-2">تمت العملية بنجاح</h4>
                <p className="text-sm text-green-700">
                  تم تصحيح حالة العمولة لـ {selectedClients.length} عميل بنجاح
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={onClose}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded"
              >
                إغلاق
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionStatusModal;